/**
 * Módulo: Team List (Sócios / Equipe)
 * Desktop: grid de 4 colunas. Ao clicar num membro com descrição, abre uma
 * caixinha posicionada de forma absoluta ao lado dele.
 * Mobile: Swiper (slider) com setas — inicializado apenas no mobile via
 * matchMedia + destroy, igual ao columns-slider.
 */

import Swiper from 'swiper';
import { Navigation } from 'swiper/modules';

const MOBILE_MAX = 767.98;

export function initTeamList() {
  document.querySelectorAll('.team-list').forEach(wrapper => {
    const swiperEl = wrapper.querySelector('.team-list__swiper');
    const grid     = wrapper.querySelector('.team-list__grid');
    const body     = wrapper.querySelector('.team-list__body');
    const detail   = wrapper.querySelector('.team-list__detail');
    if (!swiperEl || !grid || !body) return;

    const items = Array.from(wrapper.querySelectorAll('.team-list__item'));
    const mqMobile  = window.matchMedia(`(max-width: ${MOBILE_MAX}px)`);
    const isDesktop = () => !mqMobile.matches;

    // ── Slider (mobile) ──────────────────────────
    function initSwiper() {
      if (swiperEl.swiper) return;
      const prevEl = wrapper.querySelector('.team-list__btn--prev');
      const nextEl = wrapper.querySelector('.team-list__btn--next');

      requestAnimationFrame(() => {
        if (swiperEl.swiper) return;
        const slideW = body.clientWidth;
        items.forEach(i => { i.style.width = slideW + 'px'; });
        new Swiper(swiperEl, {
          modules: [Navigation],
          slidesPerView: 1,
          spaceBetween: 0,
          loop: false,
          cssMode: true,
          navigation: { prevEl, nextEl },
          a11y: {
            prevSlideMessage: 'Membro anterior',
            nextSlideMessage: 'Próximo membro',
          },
        });
      });
    }

    function destroySwiper() {
      if (swiperEl.swiper) swiperEl.swiper.destroy(true, true);
    }

    // ── Caixinha de detalhe (desktop) ────────────
    let activeItem = null;

    function positionDetail(item) {
      const bodyRect = body.getBoundingClientRect();
      const rect     = item.getBoundingClientRect();
      const top      = rect.top - bodyRect.top;
      const left     = rect.left - bodyRect.left;
      const colW     = rect.width;
      const gap      = parseFloat(getComputedStyle(grid).columnGap) || 24;
      const boxW     = colW * 2 + gap;
      const colIndex = Math.round(left / (colW + gap));

      // Coluna 0 ou 1 → caixa à direita; demais → à esquerda.
      let boxLeft = colIndex <= 1 ? left + colW + gap : left - boxW - gap;
      if (boxLeft < 0) boxLeft = left + colW + gap;

      detail.style.top   = `${top}px`;
      detail.style.left  = `${boxLeft}px`;
      detail.style.width = `${boxW}px`;
    }

    function openDetail(item) {
      const name = item.querySelector('.team-list__name');
      const role = item.querySelector('.team-list__role');
      const desc = item.querySelector('.team-list__item-desc');

      detail.querySelector('.team-list__detail-name').textContent = name ? name.textContent : '';
      detail.querySelector('.team-list__detail-role').textContent = role ? role.textContent : '';
      detail.querySelector('.team-list__detail-text').innerHTML   = desc ? desc.innerHTML : '';

      items.forEach(i => i.classList.remove('is-active'));
      item.classList.add('is-active');

      if (isDesktop()) {
        // Desktop: caixa posicionada ao lado do membro no grid.
        detail.classList.remove('team-list__detail--overlay');
        positionDetail(item);
      } else {
        // Mobile: overlay cobrindo a área do slider.
        detail.classList.add('team-list__detail--overlay');
        detail.style.top = '';
        detail.style.left = '';
        detail.style.width = '';
      }

      detail.classList.add('is-open');
      detail.setAttribute('aria-hidden', 'false');
      activeItem = item;
    }

    function closeDetail() {
      detail.classList.remove('is-open');
      detail.classList.remove('team-list__detail--overlay');
      detail.setAttribute('aria-hidden', 'true');
      if (activeItem) activeItem.classList.remove('is-active');
      activeItem = null;
    }

    if (detail) {
      // Fecha com pequeno atraso para permitir mover o mouse do membro até a
      // caixinha de detalhe (ou para outro membro) sem fechar por engano.
      let closeTimer = null;
      const cancelClose   = () => { clearTimeout(closeTimer); closeTimer = null; };
      const scheduleClose = () => { cancelClose(); closeTimer = setTimeout(closeDetail, 120); };

      items.forEach(item => {
        const desc = item.querySelector('.team-list__item-desc');
        if (!desc || !desc.innerHTML.trim()) return;

        item.addEventListener('click', e => {
          e.preventDefault();
          if (activeItem === item) closeDetail();
          else openDetail(item);
        });

        // Desktop: abrir no hover (mouse over), além do clique.
        item.addEventListener('mouseenter', () => {
          if (!isDesktop()) return;
          cancelClose();
          openDetail(item);
        });
        item.addEventListener('mouseleave', () => {
          if (!isDesktop()) return;
          scheduleClose();
        });
      });

      // Mantém aberto enquanto o mouse estiver sobre a própria caixinha.
      detail.addEventListener('mouseenter', () => { if (isDesktop()) cancelClose(); });
      detail.addEventListener('mouseleave', () => { if (isDesktop()) scheduleClose(); });

      const closeBtn = detail.querySelector('.team-list__detail-close');
      if (closeBtn) closeBtn.addEventListener('click', closeDetail);

      document.addEventListener('click', e => {
        if (activeItem && !wrapper.contains(e.target)) closeDetail();
      });

      document.addEventListener('keydown', e => {
        if (e.key === 'Escape' && activeItem) closeDetail();
      });

      window.addEventListener('resize', () => {
        if (activeItem) positionDetail(activeItem);
      });
    }

    // ── Alterna entre slider (mobile) e grid (desktop) ──
    function syncMode(matches) {
      if (matches) {
        closeDetail();
        initSwiper();
      } else {
        destroySwiper();
      }
    }

    syncMode(mqMobile.matches);
    mqMobile.addEventListener('change', e => syncMode(e.matches));
  });
}
